import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Tags, Plus, Search, Pencil, Trash2, KeyRound, Copy, RefreshCw, Check } from "lucide-react";
import { salesPricesApi, apiKeyApi, API } from "@/lib/api";
import { fmtMoney } from "@/lib/calc";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const UNITS = ["adet", "m²", "m³", "m", "kg", "ton", "paket", "takım", "saat", "gün"];
const EMPTY = { code: "", name: "", unit: "adet", unit_price: "", note: "" };

export default function SalesPriceList() {
  const { user } = useAuth();
  const isAdmin = !!user?.is_admin;
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [deleteId, setDeleteId] = useState(null);
  const [apiKey, setApiKey] = useState("");
  const [showKey, setShowKey] = useState(false);
  const [copied, setCopied] = useState(false);
  const [rotating, setRotating] = useState(false);
  const publicUrl = `${API}/public/sales-prices`;

  useEffect(() => {
    if (isAdmin) apiKeyApi.get().then((d) => setApiKey(d.api_key)).catch(() => {});
  }, [isAdmin]);

  const copyKey = async () => {
    try { await navigator.clipboard.writeText(apiKey); setCopied(true); setTimeout(() => setCopied(false), 1500); }
    catch (e) { toast.error("Kopyalanamadı"); }
  };
  const rotateKey = async () => {
    if (!window.confirm("Yeni anahtar üretilsin mi? Eski anahtar hemen geçersiz olur ve diğer siteyi güncellemeniz gerekir.")) return;
    setRotating(true);
    try { const d = await apiKeyApi.rotate(); setApiKey(d.api_key); setShowKey(true); toast.success("Yeni API anahtarı üretildi"); }
    catch (e) { toast.error("Anahtar yenilenemedi"); }
    finally { setRotating(false); }
  };

  const load = async (q) => {
    setLoading(true);
    try { setItems(await salesPricesApi.list(q)); }
    catch (e) { toast.error("Fiyat listesi yüklenemedi"); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    const t = setTimeout(() => load(search), 250);
    return () => clearTimeout(t);
  }, [search]);

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (m) => { setEditing(m); setForm({ ...m, unit_price: String(m.unit_price) }); setOpen(true); };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Ürün adı gerekli");
    const payload = { code: form.code || "", name: form.name.trim(), unit: form.unit || "adet", unit_price: parseFloat(form.unit_price) || 0, note: form.note || "", currency: "EUR" };
    try {
      if (editing) { await salesPricesApi.update(editing.id, payload); toast.success("Güncellendi"); }
      else { await salesPricesApi.create(payload); toast.success("Eklendi"); }
      setOpen(false); load(search);
    } catch (e) { toast.error("Kaydedilemedi"); }
  };

  const confirmDelete = async () => {
    try { await salesPricesApi.remove(deleteId); toast.success("Silindi"); setDeleteId(null); load(search); }
    catch (e) { toast.error("Silinemedi"); }
  };

  return (
    <div className="w-full max-w-[1600px] mx-auto p-4 md:p-8 space-y-6" data-testid="sales-price-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-extrabold tracking-tight text-slate-900 flex items-center gap-2">
            <Tags className="w-7 h-7 text-amber-600" /> Satış Fiyat Listesi
          </h1>
          <p className="text-sm text-slate-500 mt-1">Direkt satış (müşteri) fiyatları. Satış tekliflerinde bu liste kullanılır.</p>
        </div>
        <button onClick={openNew} data-testid="add-sales-price-btn"
          className="inline-flex items-center gap-2 rounded-md bg-amber-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-amber-700 transition-colors">
          <Plus className="w-4 h-4" /> Ürün Ekle
        </button>
      </div>

      <div className="relative max-w-md">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input data-testid="sales-price-search" value={search} onChange={(e) => setSearch(e.target.value)}
          placeholder="Ürün adı veya not ara…" className="pl-9 bg-white" />
      </div>

      {isAdmin && (
        <div className="rounded-lg border border-slate-200 bg-slate-50 p-5" data-testid="api-access-card">
          <div className="flex items-center gap-2 mb-1">
            <KeyRound className="w-5 h-5 text-amber-600" />
            <h2 className="font-heading text-base font-bold text-slate-900">Dış Erişim API'si</h2>
          </div>
          <p className="text-xs text-slate-500 mb-3">
            Başka bir site bu fiyat listesini otomatik çekebilir. İstek başlığında <code className="bg-slate-200 px-1 rounded">X-API-Key</code> göndermelidir.
          </p>
          <div className="grid gap-2 max-w-3xl">
            <div className="flex items-center gap-2">
              <span className="text-xs font-semibold text-slate-500 w-20 shrink-0">Endpoint</span>
              <code className="flex-1 truncate rounded border border-slate-200 bg-white px-2 py-1.5 text-xs text-slate-700" data-testid="api-endpoint-url">{publicUrl}</code>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs font-semibold text-slate-500 w-20 shrink-0">API Anahtarı</span>
              <code className="flex-1 truncate rounded border border-slate-200 bg-white px-2 py-1.5 text-xs text-slate-700 tabular-nums" data-testid="api-key-value">
                {apiKey ? (showKey ? apiKey : "•".repeat(Math.min(apiKey.length, 44))) : "…"}
              </code>
              <button onClick={() => setShowKey((s) => !s)} data-testid="toggle-api-key"
                className="rounded-md border border-slate-300 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-100">
                {showKey ? "Gizle" : "Göster"}
              </button>
              <button onClick={copyKey} data-testid="copy-api-key" title="Kopyala"
                className="rounded-md border border-slate-300 bg-white p-1.5 text-slate-600 hover:bg-slate-100">
                {copied ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}
              </button>
              <button onClick={rotateKey} disabled={rotating} data-testid="rotate-api-key"
                className="inline-flex items-center gap-1.5 rounded-md bg-slate-800 px-3 py-1.5 text-xs font-medium text-white hover:bg-slate-900 disabled:opacity-50">
                <RefreshCw className={`w-3.5 h-3.5 ${rotating ? "animate-spin" : ""}`} /> Yenile
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white shadow-sm">
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="bg-slate-100">
              {["Kod", "Ürün", "Birim", "Satış Fiyatı", "Not", "Güncellendi", ""].map((h, i) => (
                <th key={i} className={`px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500 border-b border-slate-200 ${i === 3 ? "text-right" : ""}`}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-400">Yükleniyor…</td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-400" data-testid="sales-price-empty">Henüz ürün yok. İlk satış fiyatını ekleyin.</td></tr>
            ) : items.map((m) => (
              <tr key={m.id} className="hover:bg-slate-50 transition-colors" data-testid={`sales-price-row-${m.id}`}>
                <td className="px-4 py-3 text-sm font-mono text-slate-500">{m.code || "—"}</td>
                <td className="px-4 py-3 text-sm font-semibold text-slate-900">{m.name}</td>
                <td className="px-4 py-3 text-sm text-slate-600">{m.unit}</td>
                <td className="px-4 py-3 text-sm font-semibold text-slate-900 tabular-nums text-right">
                  {fmtMoney(m.unit_price)}<span className="text-slate-400 font-normal"> / {m.unit}</span>
                </td>
                <td className="px-4 py-3 text-sm text-slate-500">{m.note || "—"}</td>
                <td className="px-4 py-3 text-xs text-slate-400">{(m.updated_at || "").slice(0, 10)}</td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-end gap-1">
                    <button onClick={() => openEdit(m)} data-testid={`sales-price-edit-${m.id}`}
                      className="p-2 rounded-md text-slate-500 hover:bg-slate-100 hover:text-slate-900" title="Düzenle"><Pencil className="w-4 h-4" /></button>
                    <button onClick={() => setDeleteId(m.id)} data-testid={`sales-price-delete-${m.id}`}
                      className="p-2 rounded-md text-red-500 hover:bg-red-50" title="Sil"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="sales-price-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{editing ? "Ürünü Düzenle" : "Ürün Ekle"}</DialogTitle>
            <DialogDescription>Satış fiyat listesine ürün ekleyin veya düzenleyin.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-2">
            <div className="grid grid-cols-3 gap-4">
              <div className="grid gap-2">
                <Label>Ürün kodu</Label>
                <Input data-testid="sales-price-code-input" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} placeholder="ör. TW-001" />
              </div>
              <div className="grid gap-2 col-span-2">
                <Label>Ürün adı</Label>
                <Input data-testid="sales-price-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="ör. Termowood Cephe Kaplama" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label>Birim</Label>
                <input list="unit-list" data-testid="sales-price-unit-input" value={form.unit}
                  onChange={(e) => setForm({ ...form, unit: e.target.value })}
                  className="rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-amber-500 bg-white" />
                <datalist id="unit-list">{UNITS.map((u) => <option key={u} value={u} />)}</datalist>
              </div>
              <div className="grid gap-2">
                <Label>Satış fiyatı (€)</Label>
                <Input data-testid="sales-price-price-input" type="number" step="0.01" value={form.unit_price}
                  onChange={(e) => setForm({ ...form, unit_price: e.target.value })} placeholder="0.00" className="tabular-nums" />
              </div>
            </div>
            <div className="grid gap-2">
              <Label>Not (opsiyonel)</Label>
              <Input data-testid="sales-price-note-input" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="ör. 1. sınıf, montaj hariç" />
            </div>
          </div>
          <DialogFooter>
            <button onClick={() => setOpen(false)} className="rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">Vazgeç</button>
            <button onClick={save} data-testid="sales-price-save-btn" className="rounded-md bg-amber-600 px-4 py-2 text-sm font-medium text-white hover:bg-amber-700">Kaydet</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Ürün silinsin mi?</AlertDialogTitle>
            <AlertDialogDescription>Bu ürünü satış fiyat listesinden kaldırır.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Vazgeç</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} data-testid="confirm-delete-sales-price" className="bg-red-600 hover:bg-red-700">Sil</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
